import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button } from '@/shared/components/Button';
import { Badge } from '@/shared/components/Badge';
import { formatDate } from '@/shared/utils/format';
import { useMyClasses } from '@/features/student-requests/hooks/useMyClasses';
import { useCreateRequest } from '@/features/student-requests/hooks/useMyRequests';
function classLabel(c) {
    return `${formatDate(c.date)} · ${c.subject_name}`;
}
/**
 * Lists the classes the student missed (absent or unmarked, in the past) that have
 * materials assigned, with a button to request access. Approving the request grants
 * the student all of that class's materials (handled server-side).
 */
export function MissedClassesPanel() {
    const { data: classes = [] } = useMyClasses();
    const createRequest = useCreateRequest();
    const [requestedIds, setRequestedIds] = useState(new Set());
    const [pendingId, setPendingId] = useState(null);
    const [error, setError] = useState('');
    const today = new Date().toISOString().slice(0, 10);
    const missed = classes.filter((c) => c.material_count > 0 &&
        c.date <= today &&
        (c.attendance_status === 'absent' || c.attendance_status === null));
    if (missed.length === 0)
        return null;
    function handleRequest(c) {
        setError('');
        setPendingId(c.id);
        createRequest.mutate({
            type: 'class_material_access',
            description: `Solicito el material de la clase a la que falté: ${classLabel(c)}.`,
            metadata: { schedule_class_id: c.id },
        }, {
            onSuccess: () => {
                setRequestedIds((prev) => new Set(prev).add(c.id));
                setPendingId(null);
            },
            onError: () => {
                setError('No se pudo enviar la solicitud. Inténtalo de nuevo.');
                setPendingId(null);
            },
        });
    }
    return (_jsxs("section", { className: "mb-8", children: [_jsx("h2", { className: "mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300", children: "Clases a las que has faltado" }), _jsx("p", { className: "mb-3 text-xs text-gray-500 dark:text-gray-400", children: "Solicita el material de las clases con material asignado a las que no asististe." }), error && _jsx("p", { role: "alert", className: "mb-3 text-sm text-red-600 dark:text-red-400", children: error }), _jsx("div", { className: "flex flex-col gap-2", children: missed.map((c) => {
                    const requested = requestedIds.has(c.id);
                    return (_jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900", children: [_jsxs("div", { children: [_jsx("p", { className: "text-sm font-medium text-gray-900 dark:text-gray-100", children: classLabel(c) }), _jsxs("p", { className: "text-xs text-gray-500 dark:text-gray-400", children: [c.material_count, " material", c.material_count !== 1 ? 'es' : '', " asignado", c.material_count !== 1 ? 's' : ''] })] }), requested ? (_jsx(Badge, { variant: "success", children: "Solicitado" })) : (_jsx(Button, { size: "sm", variant: "secondary", loading: pendingId === c.id, onClick: () => handleRequest(c), className: "w-full sm:w-auto", children: "Solicitar material" }))] }, c.id));
                }) })] }));
}
//# sourceMappingURL=MissedClassesPanel.js.map